<template>
    <div class="richtext-editor">
        <YuqueRichText ref="editorRef" @onChange="handleChange" @onLoad="handleLoad" :config="editorConfig" />
    </div>
</template>

<script setup>
import { shallowRef, onMounted } from 'vue'
import { YuqueRichText } from 'yuque-rich-text'

const props = defineProps({
    placeholder: {
        type: String,
        default: '开始编写内容...'
    }
})

const emit = defineEmits(['change', 'load'])

const editorRef = shallowRef() // 使用shallowRef避免深度响应式
let isMounted = false
let isEditorReady = false

// 编辑器配置 - 简化配置，禁用上传功能
const editorConfig = {
    // 基础配置
    placeholder: props.placeholder,
    // 禁用上传功能
    upload: false
}

onMounted(() => {
    isMounted = true
})



// 处理编辑器加载完成
const handleLoad = () => {
    isEditorReady = true
    emit('load')
    console.log('富文本编辑器加载完成')
}

// 处理内容变化
const handleChange = (value) => {
    emit('change', value)
}



// 暴露编辑器方法
defineExpose({
    // 获取编辑器内容 - 根据语雀官方文档，尝试多种格式
    getContent: (type = 'lake') => {
        if (editorRef.value && isMounted && isEditorReady) {
            console.log('📝 尝试获取富文本内容，目标格式:', type)

            // 根据官方文档，尝试不同的格式参数
            const formats = [
                'lake',           // 标准Lake格式
                'text/lake',      // MIME类型
                'raw',            // 原始格式
                'json',           // JSON格式
                'data'            // 数据格式
            ]

            for (const format of formats) {
                try {
                    console.log(`🔍 尝试格式: ${format}`)
                    const content = editorRef.value.getContent(format)

                    if (content && content !== '') {
                        console.log(`✅ 格式 ${format} 成功:`)
                        console.log('📏 内容长度:', content?.length || 0)
                        console.log('🔍 内容类型:', typeof content)
                        console.log('🔍 内容预览:', content?.substring ? content.substring(0, 300) : JSON.stringify(content).substring(0, 300))

                        // 如果是对象，可能需要序列化
                        if (typeof content === 'object') {
                            const serialized = JSON.stringify(content)
                            console.log('📦 对象已序列化，长度:', serialized.length)
                            return serialized
                        }

                        return content
                    }
                } catch (error) {
                    console.warn(`⚠️ 格式 ${format} 失败:`, error.message)
                    continue
                }
            }

            // 所有Lake相关格式都失败，最后尝试HTML
            try {
                console.log('🔄 所有Lake格式都失败，降级到HTML')
                const htmlContent = editorRef.value.getContent('html')
                console.log('✅ HTML格式获取成功，长度:', htmlContent?.length || 0)
                return htmlContent
            } catch (htmlError) {
                console.error('❌ HTML格式也失败:', htmlError.message)
                return ''
            }
        }
        console.warn('编辑器未准备好，无法获取内容')
        return ''
    },
    // 设置编辑器内容 - 支持多种格式
    setContent: (content, type = 'text/lake') => {
        console.log('设置富文本内容，格式:', type, '长度:', content?.length || 0)
        if (editorRef.value && isMounted && isEditorReady) {
            try {
                console.log('设置富文本编辑器内容:', content?.substring(0, 100) + '...')
                editorRef.value.setContent(content, type)
            } catch (error) {
                console.warn('setContent error:', error)
                // 如果Lake格式失败，尝试HTML格式
                if (type === 'text/lake') {
                    try {
                        console.log('Lake格式失败，尝试HTML格式')
                        editorRef.value.setContent(content, 'text/html')
                    } catch (htmlError) {
                        console.warn('HTML格式也失败:', htmlError)
                    }
                }
            }
        } else {
            console.warn('编辑器还未准备好，稍后重试...')
            // 如果编辑器还没准备好，等待一下再试
            setTimeout(() => {
                if (editorRef.value && isMounted && isEditorReady) {
                    try {
                        console.log('延迟设置富文本编辑器内容:', content?.substring(0, 100) + '...')
                        editorRef.value.setContent(content, type)
                    } catch (error) {
                        console.warn('延迟setContent error:', error)
                        // 如果Lake格式失败，尝试HTML格式
                        if (type === 'text/lake') {
                            try {
                                console.log('延迟Lake格式失败，尝试HTML格式')
                                editorRef.value.setContent(content, 'text/html')
                            } catch (htmlError) {
                                console.warn('延迟HTML格式也失败:', htmlError)
                            }
                        }
                    }
                }
            }, 1000)
        }
    },
    // 检查是否为空
    isEmpty: () => {
        if (editorRef.value && isMounted) {
            return editorRef.value.isEmpty()
        }
        return true
    },
    // 获取字数统计
    wordCount: () => {
        if (editorRef.value && isMounted) {
            return editorRef.value.wordCount()
        }
        return 0
    },
    // 聚焦到编辑器开头
    focusToStart: (offset = 0) => {
        if (editorRef.value && isMounted) {
            editorRef.value.focusToStart(offset)
        }
    }
})
</script>

<style scoped>
.richtext-editor {
    width: 100%;
    height: 100%;
    border: none;
    overflow: hidden;
    background: white;
    display: flex;
    flex-direction: column;
}

/* 让语雀编辑器占满容器 */
.richtext-editor :deep(.yuque-editor) {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.richtext-editor :deep(.yuque-editor-content) {
    flex: 1;
    overflow-y: auto;
}
</style>